import { createClient } from '@supabase/supabase-js';
import { Database } from './database.types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if environment variables are properly configured
const isConfigured = supabaseUrl && supabaseAnonKey && 
  supabaseUrl !== 'https://placeholder.supabase.co' && 
  supabaseAnonKey !== 'placeholder';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Missing Supabase environment variables. Please connect your Supabase project first.');
}

// Create a single Supabase client instance
export const supabase = isConfigured ? createClient<Database>(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
      storageKey: 'pmaster-auth'
    },
    global: {
      headers: {
        'X-Client-Info': 'pmaster-web'
      },
      fetch: (url, options = {}) => {
        return fetch(url, {
          ...options,
          signal: options.signal || AbortSignal.timeout(10000)
        }).catch(err => {
          if (err.name === 'AbortError' || err.message.includes('Failed to fetch')) {
            throw new Error('Failed to connect to Supabase. Please check your project status and URL configuration.');
          }
          throw err;
        });
      }
    },
    realtime: {
      params: {
        eventsPerSecond: 10
      }
    },
    db: {
      schema: 'public'
    }
  }
) : {
  // Mock client for unconfigured state
  auth: {
    getSession: () => Promise.resolve({ data: { session: null }, error: null }),
    onAuthStateChange: () => ({ data: { subscription: { unsubscribe: () => {} } } }),
    signInWithOAuth: () => Promise.reject(new Error('Supabase not configured')),
    signInWithPassword: () => Promise.reject(new Error('Supabase not configured')),
    signOut: () => Promise.reject(new Error('Supabase not configured'))
  }
} as any;

// Export configuration status
export const isSupabaseConfigured = isConfigured;

// Helper function to handle Supabase errors gracefully
export const handleSupabaseError = (error: unknown) => {
  if (error instanceof Error) {
    // Network errors
    if (error.message === 'Failed to fetch' || error.message.includes('refused to connect') || error.message.includes('NetworkError')) {
      return 'Unable to connect to Supabase. Please add http://localhost:5173 to both Site URL and Redirect URLs in your Supabase project Authentication settings.';
    }
    if (error.message.includes('CORS') || error.message.includes('CORS_ERROR')) {
      return 'Connection blocked by CORS policy. Please add http://localhost:5173 to both Site URL and Redirect URLs in your Supabase project Authentication settings.';
    }
    return error.message;
  }
  return 'An unexpected error occurred';
};